import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, money } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Masthead } from '@/components/ui/Masthead';
import { Modal, ConfirmDialog, useToast } from '@/components/ui/overlays';
import { Button, EmptyState, Spinner } from '@/components/ui/primitives';
import { Widget, WidgetFrame, type WidgetInstance, type WidgetSize, type WidgetDef } from '@/components/dashboard/Widget';
import { TasksWidget } from '@/components/dashboard/TasksWidget';

/**
 * The dashboard.
 *
 * A 12-column grid of tiles the user arranges themselves. Layout is persisted
 * per user; a role's default is used until they change something, and "reset"
 * puts it back.
 *
 * Reordering is HTML5 drag-and-drop rather than the dnd-kit used by the
 * pipeline board: tiles reorder in a simple list, there is no cross-container
 * movement or sortable-within-sortable, and the native API costs nothing extra
 * in the bundle for a screen every user loads first.
 */
export function Dashboard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();

  const [isEditing, setIsEditing] = useState(false);
  const [layout, setLayout] = useState<WidgetInstance[] | null>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<WidgetInstance | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const dragIndex = useRef<number | null>(null);

  const { data: catalogue } = useQuery({
    queryKey: ['widget-catalogue'],
    queryFn: () => api.get<WidgetDef[]>('/api/dashboard/widgets'),
  });

  const { data: stored, isLoading } = useQuery({
    queryKey: ['dashboard-layout'],
    queryFn: () => api.get<{ widgets: WidgetInstance[]; isDefault: boolean }>('/api/dashboard/layout'),
  });

  useEffect(() => {
    if (stored && layout === null) setLayout(stored.widgets);
  }, [stored, layout]);

  const save = useMutation({
    mutationFn: (widgets: WidgetInstance[]) => api.put('/api/dashboard/layout', { widgets }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['dashboard-layout'] }),
    onError: (e) => toast(e.message, 'error'),
  });

  const reset = useMutation({
    mutationFn: () => api.post<{ widgets: WidgetInstance[] }>('/api/dashboard/layout/reset'),
    onSuccess: (r) => {
      setLayout(r.widgets);
      toast('Dashboard reset to the default for your role.', 'success');
      void qc.invalidateQueries({ queryKey: ['dashboard-layout'] });
    },
  });

  /** Persist immediately — an arrangement that vanishes on refresh is worse than none. */
  const commit = (next: WidgetInstance[]) => {
    setLayout(next);
    save.mutate(next);
  };

  const byKey = useMemo(
    () => new Map((catalogue ?? []).map((w) => [w.key, w])),
    [catalogue],
  );

  const inUse = new Set((layout ?? []).map((w) => w.widget));
  const available = (catalogue ?? []).filter((w) => !inUse.has(w.key));

  const headline = useMemo(() => {
    const hour = new Date().getHours();
    return hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  }, []);

  if (isLoading || layout === null) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <Masthead
        label={`${headline}, ${user?.name.split(' ')[0] ?? ''}`}
        metric="Dashboard"
        caption={isEditing ? 'Drag tiles to rearrange · resize with S/M/L · × to remove' : undefined}
        date={new Date().toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
        action={
          <>
            {isEditing && <Button onClick={() => setIsPickerOpen(true)}>Add widget</Button>}
            {isEditing && <Button onClick={() => setConfirmReset(true)}>Reset</Button>}
            <Button variant={isEditing ? 'primary' : 'secondary'} onClick={() => setIsEditing((v) => !v)}>
              {isEditing ? 'Done' : 'Customise'}
            </Button>
          </>
        }
      />

      {layout.length === 0 ? (
        <EmptyState
          message="Your dashboard is empty."
          action={<Button variant="primary" onClick={() => { setIsEditing(true); setIsPickerOpen(true); }}>Add a widget</Button>}
        />
      ) : (
        <div className="grid grid-cols-12 gap-x-8 gap-y-6">
          {layout.map((instance, index) => (
            <WidgetFrame
              key={instance.id}
              instance={instance}
              def={byKey.get(instance.widget)}
              isEditing={isEditing}
              onRemove={() => setConfirmRemove(instance)}
              onResize={(size: WidgetSize) =>
                commit(layout.map((w) => (w.id === instance.id ? { ...w, size } : w)))
              }
              onToggleCollapse={() =>
                commit(layout.map((w) => (w.id === instance.id ? { ...w, collapsed: !w.collapsed } : w)))
              }
              onDragStart={() => { dragIndex.current = index; }}
              onDragOver={(e) => { if (isEditing) e.preventDefault(); }}
              onDrop={() => {
                const from = dragIndex.current;
                dragIndex.current = null;
                if (from === null || from === index) return;
                const next = [...layout];
                const [moved] = next.splice(from, 1);
                next.splice(index, 0, moved);
                commit(next);
              }}
            >
              {instance.widget === 'tasks_todo' ? <TasksWidget /> : <Widget widgetKey={instance.widget} />}
            </WidgetFrame>
          ))}
        </div>
      )}

      {/* ─── Widget picker ─────────────────────────────────────────────────── */}
      <Modal isOpen={isPickerOpen} onClose={() => setIsPickerOpen(false)} title="Add a widget" size="lg">
        {available.length === 0 ? (
          <EmptyState message="Every widget available to your role is already on the dashboard." />
        ) : (
          <div className="space-y-6">
            {['Tasks', 'KPI', 'Chart', 'Table', 'Activity'].map((group) => {
              const items = available.filter((w) => w.group === group);
              if (items.length === 0) return null;
              return (
                <div key={group}>
                  <h3 className="label rule mb-2 pb-1.5">{group === 'KPI' ? 'Key figures' : group}</h3>
                  <div className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                    {items.map((w) => (
                      <button
                        key={w.key}
                        onClick={() => {
                          commit([
                            ...layout,
                            { id: `${w.key}-${Math.random().toString(36).slice(2, 8)}`, widget: w.key, size: w.defaultSize, collapsed: false },
                          ]);
                          setIsPickerOpen(false);
                        }}
                        className="rule-hairline block py-2 text-left transition-colors hover:text-ink"
                      >
                        <span className="block text-body text-ink">{w.label}</span>
                        <span className="mt-0.5 block text-caption text-ink-tertiary">{w.description}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={() => commit(layout.filter((w) => w.id !== confirmRemove!.id))}
        title="Remove widget"
        confirmLabel="Remove"
        message={`Remove "${byKey.get(confirmRemove?.widget ?? '')?.label ?? 'this widget'}" from your dashboard? You can add it back from the widget picker.`}
      />

      <ConfirmDialog
        isOpen={confirmReset}
        onClose={() => setConfirmReset(false)}
        onConfirm={async () => { await reset.mutateAsync(); }}
        title="Reset dashboard"
        confirmLabel="Reset to default"
        message="This replaces your arrangement with the default layout for your role. Your customisations will be lost."
      />
    </div>
  );
}

export { money };
